import type Icon from 'ol/style/Icon';
import type { PlanningWellVariant } from '../core/types';
import { canvasIcon, HALO } from './symbolCanvas';

/**
 * «Noria / pozo» (V1.8): two cartographic 2D variants of the same `well` type, legible from 14 to 28 px, with no letter.
 * - open: traditional noria / open well, a stone curb ring around simplified water.
 * - drilled: drilled well, a concrete pad with the casing head and its discharge connection.
 * The object's colour is the water / connection colour. A thin light halo keeps both legible on satellite imagery.
 */

/** Size on the map at 100 %, in CSS pixels at render scale 1. */
export const WELL_SYMBOL_SIZE = 22;

function drawOpen(context: CanvasRenderingContext2D, u: (value: number) => number, water: string, proposed: boolean): void {
  const c = 11;
  context.beginPath(); context.arc(u(c), u(c), u(10.2), 0, Math.PI * 2); context.fillStyle = HALO; context.fill();
  // Stone curb (brocal) with joints.
  context.beginPath(); context.arc(u(c), u(c), u(9.2), 0, Math.PI * 2); context.fillStyle = '#9b9080'; context.fill();
  context.strokeStyle = '#4a443c'; context.lineWidth = u(1); context.setLineDash(proposed ? [u(2.2), u(1.6)] : []); context.stroke(); context.setLineDash([]);
  context.strokeStyle = '#5c554b'; context.lineWidth = u(0.7);
  for (let index = 0; index < 8; index += 1) {
    const angle = index * Math.PI / 4 + Math.PI / 8;
    context.beginPath(); context.moveTo(u(c + Math.cos(angle) * 6.3), u(c + Math.sin(angle) * 6.3)); context.lineTo(u(c + Math.cos(angle) * 9.1), u(c + Math.sin(angle) * 9.1)); context.stroke();
  }
  // Water with a simplified ripple.
  context.beginPath(); context.arc(u(c), u(c), u(6.1), 0, Math.PI * 2); context.fillStyle = water; context.fill();
  context.strokeStyle = '#2e2a25'; context.lineWidth = u(0.7); context.stroke();
  context.strokeStyle = 'rgba(255,255,255,0.85)'; context.lineWidth = u(1); context.lineCap = 'round';
  context.beginPath(); context.moveTo(u(7.6), u(10.4)); context.quadraticCurveTo(u(9.3), u(9), u(11), u(10.4)); context.quadraticCurveTo(u(12.7), u(11.8), u(14.4), u(10.4)); context.stroke();
}

function drawDrilled(context: CanvasRenderingContext2D, u: (value: number) => number, connection: string, proposed: boolean): void {
  // Concrete pad with halo.
  context.beginPath(); context.roundRect(u(0.8), u(0.8), u(20.4), u(20.4), u(3)); context.fillStyle = HALO; context.fill();
  context.beginPath(); context.roundRect(u(2), u(2), u(18), u(18), u(2.2)); context.fillStyle = '#d4cfc2'; context.fill();
  context.strokeStyle = '#4f4d48'; context.lineWidth = u(1); context.setLineDash(proposed ? [u(2.2), u(1.6)] : []); context.stroke(); context.setLineDash([]);
  // Discharge connection with flange, then the casing head on top of it.
  context.strokeStyle = connection; context.lineWidth = u(2.6); context.lineCap = 'butt';
  context.beginPath(); context.moveTo(u(11), u(11)); context.lineTo(u(19.4), u(11)); context.stroke();
  context.beginPath(); context.roundRect(u(17.6), u(8.4), u(1.8), u(5.2), u(0.4)); context.fillStyle = '#263238'; context.fill();
  context.beginPath(); context.arc(u(11), u(11), u(5), 0, Math.PI * 2);
  const metal = context.createLinearGradient(u(6), u(6), u(16), u(16)); metal.addColorStop(0, '#c9d0d6'); metal.addColorStop(1, '#5f6a73');
  context.fillStyle = metal; context.fill(); context.strokeStyle = '#2f363c'; context.lineWidth = u(0.9); context.stroke();
  context.beginPath(); context.arc(u(11), u(11), u(2.4), 0, Math.PI * 2); context.fillStyle = connection; context.fill();
  context.strokeStyle = '#ffffff'; context.lineWidth = u(0.6); context.stroke();
}

/** Icon of a variant at `size` (symbol scale × render scale); null where no 2D canvas exists (callers fall back). */
export function wellIcon(variant: PlanningWellVariant, color: string, proposed: boolean, size = 1, opacity = 1): Icon | null {
  return canvasIcon(`well|${variant}|${color}|${proposed}`, WELL_SYMBOL_SIZE, WELL_SYMBOL_SIZE, size, opacity, (context, u) => (variant === 'drilled' ? drawDrilled : drawOpen)(context, u, color, proposed));
}
